import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api } from '../api/client';
import { useMemo, useState } from 'react';
import TableToolbar from './TableToolbar';
import SubTabs from './SubTabs';

type Supplier = {
  id: number;
  name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
};

const SuppliersPage = () => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset } = useForm<Omit<Supplier, 'id'>>();
  const suppliersQuery = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<Supplier[]>('/api/suppliers')
  });

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<keyof Supplier>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [activeTab, setActiveTab] = useState<'directory' | 'invite'>('directory');
  const suppliers = useMemo(() => {
    const rows = (suppliersQuery.data ?? []).filter((s) => {
      if (!filter) return true;
      const f = filter.toLowerCase();
      const hay = [s.name, s.contact_name ?? '', s.email ?? '', s.phone ?? '', s.notes ?? '']
        .join(' ')
        .toLowerCase();
      return hay.includes(f);
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => String(a[sortKey] ?? '').localeCompare(String(b[sortKey] ?? '')) * dir);
  }, [suppliersQuery.data, filter, sortKey, sortDir]);

  const createSupplier = useMutation({
    mutationFn: (values: Omit<Supplier, 'id'>) => api.post<Supplier>('/api/suppliers', values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      reset();
    }
  });

  return (
    <div>
      <h2>Suppliers</h2>
      <p style={{ color: '#64748b' }}>
        Track green coffee and packaging partners. Supplier contact data lives in KV for fast
        global delivery while D1 handles transactional data.
      </p>

      <SubTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'directory', label: 'Supplier Directory', badge: suppliers.length },
          { id: 'invite', label: 'Invite Supplier' }
        ]}
      />

      {activeTab === 'directory' ? (
        <section style={{ marginTop: 8 }}>
          <h3>Supplier directory</h3>
          <TableToolbar value={filter} onChange={setFilter} placeholder="Filter suppliers…" />
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  {([
                    { key: 'name', label: 'Name' },
                    { key: 'contact_name', label: 'Contact' },
                    { key: 'email', label: 'Email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'notes', label: 'Notes' }
                  ] as Array<{ key: keyof Supplier; label: string }>).map((col) => (
                    <th
                      key={String(col.key)}
                      onClick={() => {
                        if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setSortKey(col.key);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {col.label}
                      {sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td>{supplier.name}</td>
                    <td>{supplier.contact_name ?? '—'}</td>
                    <td>{supplier.email ?? '—'}</td>
                    <td>{supplier.phone ?? '—'}</td>
                    <td>{supplier.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab === 'invite' ? (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Invite a supplier</h3>
          <form className="form-grid" onSubmit={handleSubmit((values) => createSupplier.mutate(values))}>
            <div className="form-group">
              <label>Name</label>
              <input required {...register('name', { required: true })} />
            </div>
            <div className="form-group">
              <label>Contact name</label>
              <input {...register('contact_name')} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" {...register('email')} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input {...register('phone')} />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Address</label>
              <textarea rows={3} {...register('address')} />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Notes</label>
              <textarea rows={3} {...register('notes')} />
            </div>
            <button className="btn primary" type="submit" disabled={createSupplier.isPending}>
              {createSupplier.isPending ? 'Saving…' : 'Add supplier'}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default SuppliersPage;
